import { useEffect, useMemo, useState } from "react";
import DataTable, { Column } from "@/components/ui/DataTable";
import { Badge } from "@/components/ui/badge";
import Dropdown from "@/components/ui/dropdown";
import { useGetOrdersQuery } from "@/redux/services/order/order.api";
import {
  Order,
  OrderStatus,
  OrderType,
} from "@/redux/services/order/order.type";
import { useGetRestaurantTablesQuery } from "@/redux/services/table/table.api";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import {
  formatPrice,
  getOrderStatusText,
  getOrderTotal,
  getOrderTypeLabel,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { endOfDay, format, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import OrderDetailsDrawer from "./OrderDetailsDrawer";
import { DateRangePicker } from "@/components/Dashboard/DateRangePicker";
import { SearchIgnoresFiltersHint } from "@/components/common/SearchIgnoresFiltersHint";
import { filtersUnlessSearching } from "@/lib/searchScope";

interface Props {
  restaurantId: string;
}

type PaidFilter = "paid" | "unpaid" | null;

const DATE_RANGE_KEY = "orders.dateRange";

const todayRange = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  return { startDate: today, endDate: today };
};

const loadInitialRange = () => {
  try {
    const raw = localStorage.getItem(DATE_RANGE_KEY);
    if (!raw) return todayRange();
    const parsed = JSON.parse(raw) as { startDate?: string; endDate?: string };
    if (parsed.startDate && parsed.endDate) {
      return { startDate: parsed.startDate, endDate: parsed.endDate };
    }
  } catch {
    // fall through to default
  }
  return todayRange();
};

const STATUS_OPTIONS: { label: string; value: OrderStatus }[] = [
  { label: "En attente", value: "pending" },
  { label: "Acceptée", value: "accepted" },
  { label: "En préparation", value: "preparing" },
  { label: "Prête", value: "ready" },
  { label: "Servie", value: "served" },
  { label: "Récupérée", value: "picked_up" },
  { label: "Livrée", value: "delivered" },
  { label: "Annulée", value: "canceled" },
  { label: "Rejetée", value: "rejected" },
];

const OrdersTable: React.FC<Props> = ({ restaurantId }) => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const currency = useRestaurantCurrency();

  const { data: tables } = useGetRestaurantTablesQuery(
    { restaurantId: currentRestaurant?.id },
    { skip: isOwner }
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null);
  const [paidFilter, setPaidFilter] = useState<PaidFilter>(null);
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderType | null>(
    null
  );
  const [tableFilter, setTableFilter] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(() => loadInitialRange());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    localStorage.setItem(DATE_RANGE_KEY, JSON.stringify(dateRange));
  }, [dateRange]);

  const queryParams = useMemo(() => {
    const filters: Record<string, unknown> = {
      "createdAt[after]": startOfDay(
        new Date(dateRange.startDate),
      ).toISOString(),
      "createdAt[before]": endOfDay(new Date(dateRange.endDate)).toISOString(),
    };
    if (statusFilter) filters.status = statusFilter;
    if (paidFilter === "paid") filters.isPaid = true;
    else if (paidFilter === "unpaid") filters.isPaid = false;
    if (orderTypeFilter) filters.orderType = orderTypeFilter;
    if (tableFilter) filters.table = tableFilter;

    const params: Record<string, unknown> = {
      limit: pageSize,
      page,
      "order[createdAt]": sortOrder,
      // A search term looks across all dates and ignores the other filters,
      // like the cards view; the filters scope browsing only.
      ...filtersUnlessSearching(debouncedSearch, filters),
    };
    if (debouncedSearch) params.orderNumber = debouncedSearch;
    return params;
  }, [
    page,
    pageSize,
    sortOrder,
    debouncedSearch,
    statusFilter,
    paidFilter,
    orderTypeFilter,
    tableFilter,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const { data, isFetching } = useGetOrdersQuery({
    restaurantId,
    query: queryParams,
  });

  const columns: Column[] = [
    {
      key: "orderNumber",
      label: "N° commande",
      formatter: (v) => <span className="font-semibold">#{v}</span>,
    },
    {
      key: "createdAt",
      label: "Créée le",
      sortable: true,
      formatter: (v: string) =>
        format(new Date(v), "dd MMM yyyy · HH:mm", { locale: fr }),
    },
    {
      key: "orderType",
      label: "Type",
      formatter: (v: OrderType | undefined) =>
        v ? getOrderTypeLabel(v) : "—",
    },
    {
      key: "restaurantTable",
      label: "Table",
      formatter: (v: Order["restaurantTable"]) =>
        v?.code ? v.code : "—",
    },
    {
      key: "status",
      label: "Statut",
      formatter: (v: OrderStatus) => (
        <Badge status={v}>{getOrderStatusText(v)}</Badge>
      ),
    },
    {
      key: "isPaid",
      label: "Paiement",
      formatter: (v: boolean) =>
        v ? (
          <Badge
            variant="outline"
            className="ring-success text-success border-success"
          >
            Payée
          </Badge>
        ) : (
          <span className="text-muted-foreground">Non payée</span>
        ),
    },
    {
      key: "total",
      label: "Total",
      formatter: (_v, row: Order) =>
        formatPrice(getOrderTotal(row) / 100, currency),
    },
  ];

  const selectedOrder = data?.results?.find((o) => o.id === selectedOrderId);

  return (
    <>
      <div className="space-y-4">
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onDateChange={(startDate, endDate) => {
            setDateRange({ startDate, endDate });
            setPage(1);
          }}
        />
        <SearchIgnoresFiltersHint search={debouncedSearch} />

        <div className="flex items-center gap-3 flex-wrap">
        <Dropdown
          items={STATUS_OPTIONS}
          onSelect={(v) => {
            setStatusFilter(v as OrderStatus | null);
            setPage(1);
          }}
          buttonLabel="Statut"
          variant="outline"
          showSelectedValue
          selectedValue={statusFilter}
          includeClearOption
          clearOptionLabel="Tous les statuts"
        />
        <Dropdown
          items={[
            { label: "Payées", value: "paid" },
            { label: "Non payées", value: "unpaid" },
          ]}
          onSelect={(v) => {
            setPaidFilter(v as PaidFilter);
            setPage(1);
          }}
          buttonLabel="Paiement"
          variant="outline"
          showSelectedValue
          selectedValue={paidFilter}
          includeClearOption
          clearOptionLabel="Tous les paiements"
        />
        <Dropdown
          items={[
            { label: "Sur place", value: "dine_in" },
            { label: "À emporter", value: "takeaway" },
            { label: "Livraison", value: "delivery" },
          ]}
          onSelect={(v) => {
            setOrderTypeFilter(v as OrderType | null);
            setPage(1);
          }}
          buttonLabel="Type"
          variant="outline"
          showSelectedValue
          selectedValue={orderTypeFilter}
          includeClearOption
          clearOptionLabel="Tous les types"
        />
        {!isOwner && (
          <Dropdown
            items={
              tables?.results?.map((t) => ({
                label: t.code,
                value: t.code,
              })) ?? []
            }
            onSelect={(v) => {
              setTableFilter(v as string | null);
              setPage(1);
            }}
            buttonLabel="Table"
            variant="outline"
            showSelectedValue
            selectedValue={tableFilter}
            includeClearOption
            clearOptionLabel="Toutes les tables"
          />
        )}
      </div>

      <DataTable
        data={data?.results ?? []}
        columns={columns}
        loading={isFetching}
        totalCount={data?.count ?? 0}
        currentPage={page}
        pageSize={pageSize}
        sortBy="createdAt"
        sortOrder={sortOrder}
        searchValue={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(1);
        }}
        onSort={(_col, order) => setSortOrder(order)}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(row) => setSelectedOrderId(row.id)}
        emptyMessage="Aucune commande"
      />
      </div>

      {selectedOrder && (
        <OrderDetailsDrawer
          orderId={selectedOrder.id}
          orderNumber={selectedOrder.orderNumber}
          isOpen={!!selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </>
  );
};

export default OrdersTable;
